import inter from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url';

/*
 * Stripe's Payment Element at checkout (components/card/order/stripe), from Stripe.js, loaded once per page. It opens
 * on the order's PaymentIntent (made with the page, at its total), in the site's colours and font.
 *
 * Pay asks the site first (Checkout::stripeReady: the total worked out again, the address there); then Stripe takes
 * the payment, and the site is told (Checkout::stripePaid), which opens the order as paid. A way to pay that leaves for
 * its bank's page comes back to the order's own address, which asks Stripe how it went. A new total (a code, the
 * address) is fetched by the form from Stripe, as the site has already changed the PaymentIntent's amount.
 */
Alpine.data('stripePayment', (key, secret, details, returnUrl) => ({
    failed: false,   // Stripe.js didn't load
    ready: false,    // the form is drawn
    paying: false,

    async init() {
        window.stripeJs ??= new Promise((resolve, reject) => {
            const script = Object.assign(document.createElement('script'), {src: 'https://js.stripe.com/v3/', async: true, onload: resolve, onerror: reject});
            document.head.append(script);
        });

        try {
            await window.stripeJs;
        } catch {
            this.failed = true;
            return;
        }

        // Kept here, not on `this`: Stripe's objects mustn't be wrapped in Alpine's reactive proxy
        const stripe = window.Stripe(key);
        const elements = stripe.elements({clientSecret: secret, appearance: appearance(), fonts: [{family: 'Inter', src: `url(${new URL(inter, location.href)})`, weight: '100 900'}]});
        const element = elements.create('payment', {layout: 'tabs', defaultValues: {billingDetails: details}});
        element.on('ready', () => this.ready = true);
        element.on('loaderror', () => this.failed = true);
        element.mount(this.$refs.element);

        this.update = () => elements.fetchUpdates();

        this.pay = async () => {
            if (this.paying) return;
            this.paying = true;

            try {
                const {error: invalid} = await elements.submit();
                if (invalid || !await this.$wire.stripeReady()) return;

                const {error} = await stripe.confirmPayment({elements, confirmParams: {return_url: returnUrl}, redirect: 'if_required'});
                if (error) {
                    // A card refused or a form left unfinished says why in Stripe's words; anything else plainly
                    Toaster.error(['card_error', 'validation_error'].includes(error.type) ? error.message : 'Stripe didn\'t take the payment. Nothing was charged; please try again.');
                    return;
                }

                await this.$wire.stripePaid();
            } finally {
                this.paying = false;
            }
        };
    },

    update() {},

    pay() {},
}));

/** The site's colours (whatever the theme), as Stripe takes them: rgb() from the page's own tokens. */
function appearance() {
    const canvas = document.createElement('canvas').getContext('2d', {willReadFrequently: true});
    const rgb = (token) => {
        canvas.clearRect(0, 0, 1, 1);
        canvas.fillStyle = getComputedStyle(document.documentElement).getPropertyValue(token).trim() || '#000';
        canvas.fillRect(0, 0, 1, 1);
        const [r, g, b] = canvas.getImageData(0, 0, 1, 1).data;
        return {css: `rgb(${r}, ${g}, ${b})`, light: (0.299 * r + 0.587 * g + 0.114 * b) > 128};
    };

    const surface = rgb('--color-surface');
    return {
        theme: surface.light ? 'stripe' : 'night',
        variables: {
            colorPrimary: rgb('--color-primary').css,
            colorBackground: surface.css,
            colorText: rgb('--color-ink').css,
            colorTextSecondary: rgb('--color-muted').css,
            colorDanger: rgb('--color-danger').css,
            fontFamily: 'Inter, system-ui, sans-serif',
            borderRadius: '12px',
            spacingUnit: '4px',
        },
        rules: {
            '.Input': {backgroundColor: rgb('--color-surface-raised').css, border: `1px solid ${rgb('--color-line').css}`, boxShadow: 'none'},
            '.Tab': {backgroundColor: rgb('--color-surface-raised').css, border: `1px solid ${rgb('--color-line').css}`, boxShadow: 'none'},
        },
    };
}
